/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect, useRef } from 'react';
import {
  Button,
  CodeEditor,
  type Extension,
  TextSubtitle,
  Stack,
  CodeEditorWC,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '../..';

import './editor-modal.scss';

export type EditorModalProps = GenericModalProps & {
  code?: string;
  title?: string;
  langs?: Extension[];
  progress?: number;
  loading?: boolean;
  onSave?: (code: string) => void;
};

export const EditorModal: FC<EditorModalProps> = ({
  modalRef,
  code,
  title,
  langs,
  loading,
  progress,
  onClose,
  onSave,
}) => {
  const editorRef = useRef<CodeEditorWC>(null);

  useEffect(() => {
    if (progress && progress >= 100 && !loading) {
      onClose?.();
    }
  }, [progress, loading, onClose]);

  const handleSave = () => {
    onSave?.(editorRef.current?.getValue() || '');
    onClose?.();
  };

  return (
    <GenericModal
      test-id="editor-modal"
      modalRef={modalRef}
      size="large"
      hideCloseBtn
      stayOnClickOutside
      stayOnEsc
      expandable
      className="c-editor-modal"
    >
      <TextSubtitle size="small" slot="title">
        {title}
      </TextSubtitle>

      <CodeEditor
        // @ts-ignore
        ref={editorRef}
        extensions={langs}
        value={code || ''}
        height="calc(100vh - 124px)"
        theme="vscodeDark"
        disabled={loading}
      />

      <Stack justifyContent="end" gutter="small" slot="footer">
        <Button
          size="large"
          variant="secondary"
          onClick={onClose}
          disabled={loading}
        >
          <TranslationComponent i18nKey="pro-studio/actions:editorModal.cancel">
            Cancel
          </TranslationComponent>
        </Button>
        <Button size="large" onClick={handleSave} disabled={loading}>
          <TranslationComponent
            i18nKey={
              loading && progress
                ? 'pro-studio/actions:editorModal.save.progress'
                : 'pro-studio/actions:editorModal.save.label'
            }
          >
            {loading ? 'Saving' : 'Save'}
          </TranslationComponent>
          {loading && progress && `: ${progress}%`}
        </Button>
      </Stack>
    </GenericModal>
  );
};
